/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePickupGroupsList.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing the Pickup Groups list, integrating with useDataGrid and pickupGroupsApi.
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { fetchPickupGroups } from '../api/pickupGroupsApi';
import type { PickupGroup } from '../types';

interface UsePickupGroupsListOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialColumns?: any[];
  initialSortBy?: string;
  initialSortOrder?: 'asc' | 'desc';
  initialLimit?: number;
  domainId?: number;
}

export function usePickupGroupsList({
  initialColumns = [],
  initialSortBy = 'name',
  initialSortOrder = 'asc',
  initialLimit = 10,
  domainId
}: UsePickupGroupsListOptions = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchPickupGroups({
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          search: params.search,
          domainId
        });

        return {
          data: result.data,
          pagination: result.pagination
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : t('pickupGroups.messages.loadError');
        showError(message);
        throw error;
      }
    },
    [showError, t, domainId]
  );

  const availableColumns = useMemo(() => {
    if (initialColumns.length > 0) {
      return initialColumns;
    }

    return [
      {
        key: 'name',
        label: t('pickupGroups.columns.name'),
        visible: true,
        sortable: true
      },
      {
        key: 'members',
        label: t('pickupGroups.columns.members'),
        visible: true,
        sortable: false,
        render: (_value: unknown, item: PickupGroup) => {
          const count = item?.membersCount ?? (item?.members ? item.members.length : 0);
          return count ? <span className="text-sm">{count}</span> : '-';
        }
      },
      {
        key: 'enabled',
        label: t('pickupGroups.columns.enabled'),
        visible: true,
        sortable: true,
        render: (_value: unknown, item: PickupGroup) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item?.enabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
          >
            {item?.enabled ? t('pickupGroups.enabled') : t('pickupGroups.disabled')}
          </span>
        )
      },
      {
        key: 'actions',
        label: t('pickupGroups.columns.actions'),
        visible: true,
        sortable: false
      }
    ];
  }, [t, initialColumns]);

  const dataGrid = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy,
    initialSortOrder,
    initialLimit,
    moduleName: 'pickup-groups'
  });

  // Refresh data when domainId changes
  useEffect(() => {
    if (domainId !== undefined) {
      dataGrid.refresh();
    }
  }, [domainId]);

  return dataGrid;
}

